import { BRAND_TRAITS } from '@/lib/brands/traits';
import { localeConfig } from '@/lib/configuration/configuration';
import { getBaseUrl, X_DEFAULT_LOCALE } from './constants';

/**
 * SEO hreflang country mapping from spec §6.
 *
 * Maps language slugs (as used in the app's URL structure) to
 * the ISO 3166-1 alpha-2 country codes that should receive
 * hreflang entries for that language.
 *
 * Only locales where both the country and language exist in
 * localeConfig are included in the generated output.
 */
export const HREFLANG_COUNTRY_MAP: Record<string, string[]> = {
  de: ['DE', 'CH', 'AT', 'LI'],
  en: [
    'US',
    'GB',
    'CA',
    'AU',
    'CH',
    'SE',
    'NO',
    'NL',
    'DK',
    'KW',
    'PL',
    'RO',
    'AE',
    'HR',
    'CZ',
    'SA',
    'HU',
    'GR',
    'TR',
    'FI',
    'SI',
    'IS',
    'SG',
    'NZ',
    'ZA',
  ],
  es: ['ES', 'MX', 'HN', 'PR', 'CL', 'AR', 'CR', 'PE', 'PY', 'CO', 'DO', 'GT', 'PA', 'UY', 'VE'],
  fr: ['FR', 'BE', 'LU', 'CH', 'CA', 'MC', 'EG', 'DZ', 'CI', 'RE', 'BL', 'LC'],
  it: ['IT', 'CH'],
  pt: ['PT', 'BR'],
  ru: ['BY', 'KZ', 'KG'],
  zht: ['HK', 'TW'],
  zh: ['CN'],
  ja: ['JP'],
  ko: ['KR'],
};

const NATIVE_LANGS_BY_COUNTRY: Map<string, string[]> = (() => {
  const map = new Map<string, string[]>();
  for (const [langSlug, countries] of Object.entries(HREFLANG_COUNTRY_MAP)) {
    for (const countryCode of countries) {
      const countrySlug = countryCode.toLowerCase();
      const langs = map.get(countrySlug);
      if (langs) langs.push(langSlug);
      else map.set(countrySlug, [langSlug]);
    }
  }
  return map;
})();

/**
 * Convert a language slug to its BCP 47 / ISO 639-1 language subtag.
 * Most slugs map directly; `jp` → `ja`, `zht` → `zh`.
 */
const LANG_SLUG_TO_BCP47: Record<string, string> = {
  en: 'en',
  de: 'de',
  fr: 'fr',
  es: 'es',
  it: 'it',
  pt: 'pt',
  ru: 'ru',
  ja: 'ja',
  jp: 'ja',
  ko: 'ko',
  zh: 'zh',
  zht: 'zh',
};

/**
 * Convert a locale slug (e.g. `us-en`) to a BCP 47 hreflang tag (e.g. `en-US`).
 */
export function localeToBcp47(localeSlug: string): string {
  const [countrySlug, langSlug] = localeSlug.split('-');
  const lang = LANG_SLUG_TO_BCP47[langSlug] || langSlug;
  const country = countrySlug.toUpperCase();
  return `${lang}-${country}`;
}

export interface HreflangEntry {
  hreflang: string;
  href: string;
}

/**
 * Primary locales for hreflang, robots.txt, and sitemaps. Policy is brand-specific
 * via {@link BRAND_TRAITS.primaryLocalesPolicy} so Breitling keeps legacy behavior.
 */
export function getPrimaryLocales(): Set<string> {
  if (BRAND_TRAITS.primaryLocalesPolicy === 'live-market-fallback') {
    return getPrimaryLocalesWithLiveMarketFallback();
  }
  return getPrimaryLocalesFromHreflangMap();
}

/** Legacy Breitling: HREFLANG_COUNTRY_MAP ∩ all configured locales. */
export function getPrimaryLocalesFromHreflangMap(): Set<string> {
  const primary = new Set<string>();
  const allLocales = new Set(localeConfig.getAllLocales());

  for (const [langSlug, countries] of Object.entries(HREFLANG_COUNTRY_MAP)) {
    for (const countryCode of countries) {
      const countrySlug = countryCode.toLowerCase();
      const locale = `${countrySlug}-${langSlug}`;
      if (allLocales.has(locale)) {
        primary.add(locale);
      }
    }
  }

  return primary;
}

/**
 * Gallet: for each live country, emit mapped native locales when live; otherwise
 * fall back to the country's default live locale (English-only JP, SK, etc.).
 */
export function getPrimaryLocalesWithLiveMarketFallback(): Set<string> {
  const liveByCountry = new Map<string, Set<string>>();

  for (const country of localeConfig.getLiveMarkets()) {
    for (const locale of country.locales) {
      const langs = liveByCountry.get(country.slug) ?? new Set<string>();
      langs.add(locale.slug);
      liveByCountry.set(country.slug, langs);
    }
  }

  const primary = new Set<string>();

  for (const [countrySlug, liveLangs] of liveByCountry) {
    const liveNatives = (NATIVE_LANGS_BY_COUNTRY.get(countrySlug) ?? []).filter((lang) =>
      liveLangs.has(lang)
    );

    if (liveNatives.length > 0) {
      for (const lang of liveNatives) {
        primary.add(`${countrySlug}-${lang}`);
      }
      continue;
    }

    const fallbackLang = localeConfig.getCountryBySlug(countrySlug)?.locales[0]?.slug;
    const countryIsMapped = NATIVE_LANGS_BY_COUNTRY.has(countrySlug);
    if (!fallbackLang || !liveLangs.has(fallbackLang)) continue;
    if (!countryIsMapped && fallbackLang !== 'en') continue;

    primary.add(`${countrySlug}-${fallbackLang}`);
  }

  return primary;
}

/**
 * Get secondary (non-primary) locales — these are blocked in robots.txt
 * and excluded from sitemaps.
 */
export function getSecondaryLocales(): string[] {
  const primary = getPrimaryLocales();
  return localeConfig.getAllLocales().filter((l) => !primary.has(l));
}

/**
 * Expand activeLocales so zht and zh always appear together per spec §6.
 * If any Chinese locale is active, all Chinese locales from the primary set
 * are included.
 */
function expandChineseLocales(activeLocales: Set<string>, primary: Set<string>): Set<string> {
  const CHINESE_LANG_SLUGS = new Set(['zh', 'zht']);
  const hasAnyChinese = [...activeLocales].some((l) => {
    const lang = l.split('-')[1];
    return CHINESE_LANG_SLUGS.has(lang);
  });

  if (!hasAnyChinese) return activeLocales;

  const expanded = new Set(activeLocales);
  for (const locale of primary) {
    const lang = locale.split('-')[1];
    if (CHINESE_LANG_SLUGS.has(lang)) {
      expanded.add(locale);
    }
  }
  return expanded;
}

/**
 * Build hreflang alternate entries for a page path.
 *
 * Generates BCP 47 hreflang tags per spec §6:
 * - Only primary locales from the hreflang country mapping
 * - Self-referencing (current locale included)
 * - x-default points to us-en
 * - Chinese special case: zht and zh always appear together
 *
 * @param pagePath - Page path without locale prefix (e.g. `/about/`)
 * @param baseUrl - Base URL for absolute hrefs
 * @param activeLocales - Optional filter: only include these locales (for CMS pages)
 */
export function buildHreflangEntries(
  pagePath: string,
  baseUrl: string,
  activeLocales?: Set<string> | null
): HreflangEntry[] {
  const entries: HreflangEntry[] = [];
  const primary = getPrimaryLocales();
  const normalizedPath = pagePath.startsWith('/') ? pagePath : `/${pagePath}`;
  const trailingPath = normalizedPath.endsWith('/') ? normalizedPath : `${normalizedPath}/`;

  const effectiveLocales = activeLocales ? expandChineseLocales(activeLocales, primary) : null;

  for (const locale of primary) {
    if (effectiveLocales && !effectiveLocales.has(locale)) continue;

    const bcp47 = localeToBcp47(locale);
    const href = `${baseUrl}/${locale}${trailingPath}`;
    entries.push({ hreflang: bcp47, href });
  }

  const xDefaultHref = `${baseUrl}/${X_DEFAULT_LOCALE}${trailingPath}`;
  entries.push({ hreflang: 'x-default', href: xDefaultHref });

  return entries;
}

/**
 * Build hreflang alternates as a Record for Next.js metadata API.
 *
 * Next.js `alternates.languages` accepts `Record<string, string>` where
 * keys are BCP 47 tags (or `x-default`) and values are URLs.
 *
 * @param pagePath - Page path without locale prefix (e.g. `/about/` or empty for homepage)
 * @param baseUrl - Base URL for absolute hrefs
 * @param activeLocales - Optional filter: only include these locales (for CMS pages)
 */
export function buildHreflangAlternates(
  pagePath: string,
  baseUrl: string,
  activeLocales?: Set<string> | null
): Record<string, string> {
  const entries = buildHreflangEntries(pagePath, baseUrl, activeLocales);
  return Object.fromEntries(entries.map((e) => [e.hreflang, e.href]));
}

/**
 * Convert an array of country codes (from CMS `localizationConfiguration`) to
 * a Set of primary locale slugs suitable for the `activeLocales` parameter.
 *
 * Per BEC-4404: hreflang tags are generated for the **same language** only,
 * then intersected with the CMS localization config. This means a German page
 * in markets [AT, DE, CH] produces `de-DE`, `de-CH`, `de-AT` — not `en-CH`
 * or `fr-CH`.
 *
 * The `language` parameter restricts output to that language's group in
 * HREFLANG_COUNTRY_MAP. Chinese is special-cased: `zh` and `zht` always
 * expand together per spec §6.
 *
 * @param countryCodes - Lowercase country codes from Contentful (e.g. `['us', 'ca']`)
 * @param language - Current page's language slug (e.g. `en`, `de`, `fr`). Restricts
 *   output to same-language locales only.
 * @returns Set of locale slugs (e.g. `Set(['us-en', 'ca-en'])`) or null if empty
 */
export function countryCodesToPrimaryLocales(
  countryCodes: string[],
  language: string
): Set<string> | null {
  if (countryCodes.length === 0) return null;

  const primary = getPrimaryLocales();
  const activeCountries = new Set(countryCodes.map((c) => c.toLowerCase()));
  const result = new Set<string>();

  const CHINESE_LANG_SLUGS = new Set(['zh', 'zht']);
  const isChinese = CHINESE_LANG_SLUGS.has(language);
  const langSlugsToCheck = isChinese ? CHINESE_LANG_SLUGS : new Set([language]);

  for (const langSlug of langSlugsToCheck) {
    const countries = HREFLANG_COUNTRY_MAP[langSlug];
    if (!countries) continue;

    for (const countryCode of countries) {
      if (!activeCountries.has(countryCode.toLowerCase())) continue;
      const locale = `${countryCode.toLowerCase()}-${langSlug}`;
      if (primary.has(locale)) {
        result.add(locale);
      }
    }
  }

  return result.size > 0 ? result : null;
}

/**
 * Convenience wrapper: build hreflang alternates using the default base URL.
 *
 * Use in page-level `generateMetadata` when constructing `alternates.languages`.
 * Extracts the page path (without locale prefix) from the full path.
 *
 * @param fullPath - Full path including locale prefix (e.g. `/us-en/watches/all`)
 * @param locale - Locale slug (e.g. `us-en`)
 */
export function getPageHreflangAlternates(
  fullPath: string,
  locale: string
): Record<string, string> {
  const baseUrl = getBaseUrl();
  const prefix = `/${locale}`;
  const pagePath = fullPath.startsWith(prefix) ? fullPath.slice(prefix.length) || '/' : fullPath;
  return buildHreflangAlternates(pagePath, baseUrl);
}
